import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useOrgContext } from '@/hooks/useOrgContext';

// ── ASC schedule data layer (2026-08-15, Rolls-Royce rework) ────────────────
// Everything here rides plumbing verified live this morning:
// - rooms/bays: saas.schedule_resources (facility 16) + cr.patient_room_stays
//   for occupancy ('occupied') and turnover ('cleaning')
// - cases: cr.or_cases by LOCAL case_date
// - movement: public.advance_case_stage + assign/transfer/release_patient_room
// - edit/cancel: direct or_cases update under the case_spine_update RLS
//   policy, plus cancel_schedule_event when a schedule_event is linked.

export const ASC_FACILITY_ID = 16;
export const STAGE_ORDER = ['preop', 'inor', 'pacu', 'discharge'] as const;
export type Stage = (typeof STAGE_ORDER)[number];

export interface OrCase {
    case_id: number;
    case_number: string | null;
    patient_id: number | null;
    patient_name: string | null;
    procedure_name: string | null;
    surgeon_name: string | null;
    or_room: string | null;
    scheduled_start: string | null;
    actual_start: string | null;
    completed_at: string | null;
    estimated_duration_minutes: number | null;
    case_status: string | null;
    schedule_status: string | null;
    anesthesiologist_name: string | null;
    scrub_tech_name: string | null;
    circulating_nurse_name: string | null;
    stage: string | null;
    stage_entered_at: string | null;
    encounter_id: number | null;
    schedule_event_id: string | null;
}

export interface RoomResource {
    resource_id: string;
    resource_kind: 'preop_bay' | 'or_suite' | 'pacu_bay' | 'discharge_area';
    display_name: string;
}

export interface RoomStay {
    room_stay_id: string;
    encounter_id: number | null;
    room_resource_id: string;
    status: 'occupied' | 'cleaning';
    assigned_at: string;
}

export interface StaffMember {
    provider_id: number;
    first_name: string | null;
    last_name: string | null;
    credential: string | null;
    title: string | null;
    specialty: string | null;
}

export type StaffRole =
    | 'surgeon' | 'anesthesiologist' | 'crna' | 'preop_rn'
    | 'circulating_rn' | 'scrub_tech' | 'pacu_rn' | 'discharge_rn' | 'support';

export interface StaffAssignment {
    assignment_id: string;
    assignment_date: string;
    resource_id: string;
    resource_name: string;
    area: 'preop' | 'or' | 'pacu' | 'discharge';
    role: StaffRole;
    staff_provider_id: number | null;
    staff_name: string;
    shift_start: string | null;
    shift_end: string | null;
    notes: string | null;
}

export interface VisitType {
    appt_type_id: string;
    name: string;
    duration_mins: number | null;
    specialty: string | null;
}

export interface SlotOption {
    startsAt: string;
    endsAt: string;
}

export function useSurgicalVisitTypes() {
    const { org_id: orgId } = useOrgContext();
    return useQuery({
        queryKey: ['asc-visit-types', orgId],
        enabled: !!orgId,
        queryFn: async (): Promise<VisitType[]> => {
            const { data, error } = await supabase
                .schema('saas')
                .from('appointment_types')
                .select('appt_type_id, name, duration_mins, specialty')
                .eq('org_id', orgId!)
                .eq('is_active', true)
                .eq('care_setting', 'surgery')
                .order('name');
            if (error) throw error;
            return (data ?? []) as VisitType[];
        },
    });
}

export interface SurgeonOption {
    provider_id: number;
    first_name: string | null;
    last_name: string | null;
    specialty: string | null;
    credential: string | null;
}

// B5 (2026-08-15): surgeon pickers must show PHYSICIANS in surgical
// specialties — never Administration (the "Demoadmin, Alexis" default bug).
// credential is unpopulated on Institute providers, so specialty is the key.
export function useSurgeons() {
    const { org_id: orgId } = useOrgContext();
    return useQuery({
        queryKey: ['asc-surgeons', orgId],
        enabled: !!orgId,
        queryFn: async (): Promise<SurgeonOption[]> => {
            const { data, error } = await supabase
                .schema('cr')
                .from('providers')
                .select('provider_id, first_name, last_name, specialty, credential')
                .eq('org_id', orgId!)
                .eq('is_active', true)
                .ilike('specialty', '%surg%')
                .order('last_name');
            if (error) throw error;
            return (data ?? []) as SurgeonOption[];
        },
    });
}

export function surgeonLabel(p: SurgeonOption): string {
    const name = [p.first_name, p.last_name].filter(Boolean).join(' ');
    return p.credential ? `${name}, ${p.credential}` : name;
}

export function localToday(): string {
    return new Date().toLocaleDateString('en-CA');
}

export function useAscSchedule(dateStr: string) {
    const { org_id: orgId } = useOrgContext();

    const rooms = useQuery({
        queryKey: ['asc-rooms', orgId],
        enabled: !!orgId,
        queryFn: async (): Promise<RoomResource[]> => {
            const { data, error } = await supabase
                .schema('saas')
                .from('schedule_resources')
                .select('resource_id, resource_kind, display_name')
                .eq('org_id', orgId!)
                .eq('facility_id', ASC_FACILITY_ID)
                .in('resource_kind', ['preop_bay', 'or_suite', 'pacu_bay'])
                .eq('active', true)
                .order('display_name');
            if (error) throw error;
            return ((data ?? []) as RoomResource[]).sort((a, b) =>
                a.display_name.localeCompare(b.display_name, undefined, { numeric: true, sensitivity: 'base' }));
        },
    });

    const cases = useQuery({
        queryKey: ['asc-cases', orgId, dateStr],
        enabled: !!orgId,
        refetchInterval: 20_000,
        queryFn: async (): Promise<OrCase[]> => {
            const { data, error } = await supabase
                .schema('cr')
                .from('or_cases')
                .select('case_id, case_number, patient_id, patient_name, procedure_name, surgeon_name, or_room, scheduled_start, actual_start, completed_at, estimated_duration_minutes, case_status, schedule_status, anesthesiologist_name, scrub_tech_name, circulating_nurse_name, stage, stage_entered_at, encounter_id, schedule_event_id')
                .eq('org_id', orgId!)
                .eq('case_date', dateStr)
                .is('deleted_at', null)
                .order('scheduled_start', { ascending: true });
            if (error) throw error;
            return (data ?? []) as OrCase[];
        },
    });

    const stays = useQuery({
        queryKey: ['asc-stays', orgId],
        enabled: !!orgId,
        refetchInterval: 20_000,
        queryFn: async (): Promise<RoomStay[]> => {
            const { data, error } = await supabase
                .schema('cr')
                .from('patient_room_stays')
                .select('room_stay_id, encounter_id, room_resource_id, status, assigned_at')
                .eq('org_id', orgId!)
                .in('status', ['occupied', 'cleaning'])
                .order('assigned_at', { ascending: false, nullsFirst: false });
            if (error) throw error;
            // Room state is the room's MOST RECENT stay. Cleaning stays carry a
            // released_at, so the old `released_at IS NULL` filter dropped every
            // bay awaiting turnover — hiding them and letting the board treat a
            // dirty room as open. Take the latest stay per room instead.
            const seen = new Set<string>();
            const latest: RoomStay[] = [];
            for (const s of (data ?? []) as RoomStay[]) {
                if (seen.has(s.room_resource_id)) continue;
                seen.add(s.room_resource_id);
                latest.push(s);
            }
            return latest;
        },
    });

    return { rooms, cases, stays };
}

export function useAscStaffing(dateStr: string, enabled = true) {
    const { org_id: orgId } = useOrgContext();

    const staff = useQuery({
        queryKey: ['asc-clinical-staff', orgId],
        enabled: !!orgId && enabled,
        queryFn: async (): Promise<StaffMember[]> => {
            const { data, error } = await supabase
                .schema('cr').from('providers')
                .select('provider_id, first_name, last_name, credential, title, specialty')
                .eq('org_id', orgId!).eq('is_active', true)
                .order('last_name');
            if (error) throw error;
            return (data ?? []) as StaffMember[];
        },
    });

    const assignments = useQuery({
        queryKey: ['asc-staff-assignments', orgId, dateStr],
        enabled: !!orgId && enabled,
        refetchInterval: 30_000,
        queryFn: async (): Promise<StaffAssignment[]> => {
            const { data, error } = await supabase
                .schema('cr').from('asc_staff_assignments')
                .select('assignment_id, assignment_date, resource_id, resource_name, area, role, staff_provider_id, staff_name, shift_start, shift_end, notes')
                .eq('org_id', orgId!).eq('facility_id', ASC_FACILITY_ID)
                .eq('assignment_date', dateStr)
                .order('resource_name').order('role');
            if (error) throw error;
            return (data ?? []) as StaffAssignment[];
        },
    });

    return { staff, assignments };
}

export function useAscActions(dateStr: string) {
    const { org_id: orgId } = useOrgContext();
    const qc = useQueryClient();
    const invalidate = () => {
        void qc.invalidateQueries({ queryKey: ['asc-cases'] });
        void qc.invalidateQueries({ queryKey: ['asc-stays'] });
        void qc.invalidateQueries({ queryKey: ['asc-staff-assignments'] });
    };

    const advance = useMutation({
        mutationFn: async (input: { c: OrCase; toStage: Stage; toRoomResourceId?: string; stays: RoomStay[] }) => {
            const { c, toStage, toRoomResourceId, stays } = input;
            const { error } = await supabase.rpc('advance_case_stage', {
                p_case_id: c.case_id,
                p_to_stage: toStage,
            });
            if (error) throw error;
            if (c.encounter_id != null) {
                const open = stays.find((s) => s.encounter_id === c.encounter_id);
                if (toStage === 'discharge') {
                    if (open) {
                        const { error: relErr } = await supabase.rpc('release_patient_room', {
                            p_room_stay_id: open.room_stay_id, p_reason: 'discharged', p_org_id: orgId!,
                        });
                        if (relErr) throw relErr;
                    }
                } else if (toRoomResourceId) {
                    if (open) {
                        const { error: trErr } = await supabase.rpc('transfer_patient_room', {
                            p_room_stay_id: open.room_stay_id, p_to_room_resource_id: toRoomResourceId, p_org_id: orgId!,
                        });
                        if (trErr) throw trErr;
                    } else {
                        const { error: asErr } = await supabase.rpc('assign_patient_room', {
                            p_encounter_id: c.encounter_id, p_room_resource_id: toRoomResourceId,
                            p_schedule_event_id: c.schedule_event_id, p_org_id: orgId!,
                        });
                        if (asErr) throw asErr;
                    }
                }
            }
        },
        onSettled: invalidate,
    });

    const cancelCase = useMutation({
        mutationFn: async (input: { c: OrCase; reason: string }) => {
            const { c, reason } = input;
            if (c.schedule_event_id) {
                const { error } = await supabase.rpc('cancel_schedule_event', {
                    p_schedule_event_id: c.schedule_event_id, p_reason: reason,
                    p_expected_version: null, p_org_id: orgId!,
                });
                if (error) throw error;
            }
            const { error: updErr } = await supabase
                .schema('cr').from('or_cases')
                .update({ case_status: 'cancelled', schedule_status: 'cancelled', case_notes: reason })
                .eq('case_id', c.case_id).eq('org_id', orgId!);
            if (updErr) throw updErr;
        },
        onSettled: invalidate,
    });

    // 2026-08-15: reschedule is now ONE transaction — public.reschedule_or_case
    // moves the schedule reservation (room preserved; conflicts rejected by the
    // EXCLUDE constraint) and updates the case row atomically. The previous
    // two-request pattern could desync the case row from the reservation.
    const rescheduleCase = useMutation({
        mutationFn: async (input: { c: OrCase; newStartLocal: string; durationMins: number }) => {
            const { c, newStartLocal, durationMins } = input;
            const startsAt = new Date(newStartLocal);
            const { error } = await supabase.rpc('reschedule_or_case', {
                p_case_id: c.case_id,
                p_starts_at: startsAt.toISOString(),
                p_duration_mins: durationMins,
                p_org_id: orgId!,
            });
            if (error) throw error;
        },
        onSettled: invalidate,
    });

    const completeTurnover = useMutation({
        mutationFn: async (roomResourceId: string) => {
            const { error } = await supabase.rpc('complete_room_turnover', {
                p_room_resource_id: roomResourceId,
                p_org_id: orgId!,
            });
            if (error) throw error;
        },
        onSettled: invalidate,
    });

    const releaseRoom = useMutation({
        mutationFn: async (input: { stay: RoomStay; reason: string }) => {
            const { error } = await supabase.rpc('release_patient_room', {
                p_room_stay_id: input.stay.room_stay_id,
                p_reason: input.reason,
                p_org_id: orgId!,
            });
            if (error) throw error;
        },
        onSettled: invalidate,
    });

    const saveStaffAssignment = useMutation({
        mutationFn: async (input: {
            dateStr: string;
            resource: RoomResource;
            role: StaffRole;
            staff: StaffMember;
            shiftStart?: string;
            shiftEnd?: string;
            notes?: string;
        }) => {
            const name = staffLabel(input.staff);
            const area = input.resource.resource_kind === 'preop_bay' ? 'preop'
                : input.resource.resource_kind === 'pacu_bay' ? 'pacu'
                    : input.resource.resource_kind === 'discharge_area' ? 'discharge' : 'or';
            const { error } = await supabase
                .schema('cr').from('asc_staff_assignments')
                .upsert({
                    org_id: orgId!,
                    facility_id: ASC_FACILITY_ID,
                    assignment_date: input.dateStr,
                    resource_id: input.resource.resource_id,
                    resource_name: input.resource.display_name,
                    area,
                    role: input.role,
                    staff_provider_id: input.staff.provider_id,
                    staff_name: name,
                    shift_start: input.shiftStart || null,
                    shift_end: input.shiftEnd || null,
                    notes: input.notes?.trim() || null,
                }, { onConflict: 'org_id,assignment_date,resource_id,role,staff_provider_id' });
            if (error) {
                if (error.code === '23P01' || error.message?.includes('asc_staff_assignment_no_overlap')) {
                    throw new Error(`${name} already has overlapping coverage on this date`);
                }
                throw error;
            }
        },
        onSettled: invalidate,
    });

    const removeStaffAssignment = useMutation({
        mutationFn: async (assignmentId: string) => {
            const { error } = await supabase
                .schema('cr').from('asc_staff_assignments')
                .delete().eq('assignment_id', assignmentId).eq('org_id', orgId!);
            if (error) throw error;
        },
        onSettled: invalidate,
    });

    // B1 (2026-08-15): Book case — the mockup button that never got built.
    // find_available_slots walks the visit type's increment grid against
    // schedule_reservations; book_schedule_event reserves the OR atomically
    // (EXCLUDE constraint — double-booking is impossible) and the or_cases
    // row rides its schedule_event_id. Encounter opens at CHECK-IN, not at
    // booking, matching the Forge open_encounter model — so encounter_id is
    // null here by design.
    const findSlots = useMutation({
        mutationFn: async (input: { visitTypeId: string; dateStr: string; roomResourceId: string }): Promise<SlotOption[]> => {
            const from = new Date(`${input.dateStr}T07:00:00`);
            const to = new Date(`${input.dateStr}T18:00:00`);
            const { data, error } = await supabase.rpc('find_available_slots', {
                p_visit_type_id: input.visitTypeId,
                p_facility_id: ASC_FACILITY_ID,
                p_from: from.toISOString(),
                p_to: to.toISOString(),
                p_resource_ids: [input.roomResourceId],
                p_org_id: orgId!,
            });
            if (error) throw error;
            return ((data ?? []) as Array<{ startsAt: string; endsAt: string }>).map((s) => ({
                startsAt: s.startsAt, endsAt: s.endsAt,
            }));
        },
    });

    const bookCase = useMutation({
        mutationFn: async (input: {
            patient: { patient_id: number; name: string };
            visitType: VisitType;
            surgeon: SurgeonOption;
            room: RoomResource;
            slot: SlotOption;
            idempotencyKey: string;
        }) => {
            const { patient, visitType, surgeon, room, slot, idempotencyKey } = input;
            const durationMins = visitType.duration_mins ?? 60;
            const procedureName = visitType.name.replace(/\s*\(ASC\)\s*$/i, '');
            const surgeonName = surgeonLabel(surgeon);

            const { data: authData, error: authErr } = await supabase.auth.getUser();
            if (authErr || !authData.user) throw new Error('No authenticated session');
            const { data: member, error: memErr } = await supabase
                .schema('saas').from('org_members')
                .select('id, full_name, first_name, last_name')
                .eq('org_id', orgId!).eq('user_id', authData.user.id)
                .limit(1).single();
            if (memErr || !member) throw new Error('No active membership for this organization');
            const createdByName = member.full_name
                ?? [member.first_name, member.last_name].filter(Boolean).join(' ')
                ?? 'Unknown';

            // 2026-08-15: ONE transaction — public.book_or_case reserves the room
            // (EXCLUDE constraint, idempotency, BAA/membership gates via
            // cr.book_schedule_event) and creates the or_cases row atomically.
            // The previous two-request pattern could leave an orphan reservation
            // if the case insert failed.
            const orRoomName = roomDisplay(room.display_name.replace('Operating Room ', 'OR '));
            const { error: bookErr } = await supabase.rpc('book_or_case', {
                p_input: {
                    facility_id: ASC_FACILITY_ID,
                    patient_id: patient.patient_id,
                    visit_type_id: visitType.appt_type_id,
                    // 'surgical_case' per cr.schedule_events event_kind CHECK —
                    // the earlier 'or_case' value violated the constraint
                    // (caught by the 2026-08-15 booking dry-run).
                    event_kind: 'surgical_case',
                    owner_app: 'or',
                    origin_app: 'or',
                    starts_at: slot.startsAt,
                    ends_at: slot.endsAt,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    title: `${procedureName} — ${patient.name}`,
                    participants: [{ resource_id: room.resource_id, role: 'room', required: true }],
                },
                p_case: {
                    patient_id: patient.patient_id,
                    patient_name: patient.name,
                    procedure_name: procedureName,
                    surgeon_id: surgeon.provider_id,
                    surgeon_name: surgeonName,
                    or_room: orRoomName,
                    scheduled_start: slot.startsAt,
                    estimated_duration_minutes: durationMins,
                    created_by_id: member.id,
                    created_by_name: createdByName,
                },
                p_idempotency_key: idempotencyKey,
                p_org_id: orgId!,
            });
            if (bookErr) {
                if (bookErr.message?.includes('resource_double_booked')) {
                    throw new Error('That room was just booked for this time — pick another slot');
                }
                throw bookErr;
            }
        },
        onSettled: invalidate,
    });

    void dateStr;
    return {
        advance, cancelCase, rescheduleCase, completeTurnover, releaseRoom,
        saveStaffAssignment, removeStaffAssignment, findSlots, bookCase,
    };
}

export function nextStage(stage: string | null): Stage | null {
    const i = STAGE_ORDER.indexOf((stage ?? '') as Stage);
    if (i === -1 || i === STAGE_ORDER.length - 1) return null;
    return STAGE_ORDER[i + 1];
}

export function stageLabel(s: Stage): string {
    return s === 'preop' ? 'Pre-op' : s === 'inor' ? 'OR' : s === 'pacu' ? 'PACU' : 'Discharge';
}

// Room-name normalization (2026-08-15 review finding: 'OR 1' and 'OR-1'
// rendered as separate columns). One canonical key, one display form.
export function roomKey(name: string | null): string {
    return (name ?? '').replace(/[^a-z0-9]/gi, '').toUpperCase();
}
export function roomDisplay(name: string): string {
    const m = roomKey(name).match(/^([A-Z]+)(\d+)$/);
    return m ? `${m[1]} ${m[2]}` : name;
}
export function careTeamLine(c: OrCase): string {
    const bits: string[] = [];
    if (c.anesthesiologist_name) bits.push(`Anes ${c.anesthesiologist_name}`);
    if (c.circulating_nurse_name) bits.push(`RN ${c.circulating_nurse_name}`);
    if (c.scrub_tech_name) bits.push(`CST ${c.scrub_tech_name}`);
    return bits.join(' · ');
}

export function staffLabel(p: StaffMember): string {
    const name = [p.first_name, p.last_name].filter(Boolean).join(' ');
    return p.credential ? `${name}, ${p.credential}` : name;
}

export function staffRoleLabel(role: StaffRole): string {
    const labels: Record<StaffRole, string> = {
        surgeon: 'Surgeon', anesthesiologist: 'Anesthesiologist', crna: 'CRNA', preop_rn: 'Pre-Op RN',
        circulating_rn: 'Circulating RN', scrub_tech: 'Scrub tech',
        pacu_rn: 'PACU RN', discharge_rn: 'Discharge RN', support: 'Support',
    };
    return labels[role];
}
